import { DataObject } from '@shared/models/data-object.model';

const safeIterableContacts = state => state.company_contact_role_addresses || [];

/**
 * Compares two company_contact_role's location objects if target has present
 * @param targetLocation
 * @param sourceLocation
 */
export const compareLocationsIfPresent = (targetLocation: any, sourceLocation: any): boolean =>
  !targetLocation || (targetLocation && sourceLocation && targetLocation.location_id === sourceLocation.location_id);

/**
 * Matcher function to be used on filter, find, some, every, map to compare to
 * company_contact_role items and determine equality. target param drives the
 * equality of the match and location and contact_id are compared if passed,
 * else only company_id & role_desc are the only one "mandatory" to determine
 * equality since come company/contacts does not have either location or
 * contact_id
 *
 * @param target: company_contact_role item
 * @param source: company_contact_role item
 * @returns {boolean}
 */
export const companyContactMatcher = (target: any, source: any): boolean =>
  target.company_id === source.company_id &&
  target.role_desc === source.role_desc &&
  compareLocationsIfPresent(target.location, source.location) &&
  // compare contact_id if present, else by pass with true
  (!target.contact_id || target.contact_id === source.contact_id);

/**
 * Get the role desc and make it lowercase to compare with target role.
 *
 * @param targetRole to find
 * @param company_contact_role_addresses array
 */
export const byLowerRoleDesc = (targetRole: string, { role_desc }: { role_desc: string }) =>
  !!targetRole && role_desc?.toLowerCase() === targetRole?.toLowerCase();

export function getContactByRoleOrFirst(
  companyContactList: Array<{ role_desc: string; first_name: string }>,
  roleDescription: string,
  secondaryRole: string = null
): DataObject {
  let filteredCompanyContact = companyContactList.filter(contact => byLowerRoleDesc(roleDescription, contact));
  if (!filteredCompanyContact.length && secondaryRole) {
    filteredCompanyContact = companyContactList.filter(contact => byLowerRoleDesc(secondaryRole, contact));
  }
  if (!!filteredCompanyContact.length) {
    const companyContact = filteredCompanyContact.find(({ first_name }) => first_name != null);
    return !!companyContact ? companyContact : filteredCompanyContact[0];
  }
  return {};
}

export function getNestedContactByRoleOrFirst(
  companies: Array<{ role_desc: string; first_name: string; children: any[] }>,
  roleDescription: string,
  secondaryRole: string = null
): DataObject {
  let companiesWithGivenRole = companies.filter(company => byLowerRoleDesc(roleDescription, company));
  if (!companiesWithGivenRole.length && secondaryRole) {
    companiesWithGivenRole = companies.filter(company => byLowerRoleDesc(secondaryRole, company));
  }
  const contactsWithGivenRole = companiesWithGivenRole
    .filter(x => x.children.length > 0)
    .reduce((acc, nextCompany) => [...acc, ...(nextCompany.children || [])], []);
  const contact =
    contactsWithGivenRole.find(contactInstance => contactInstance.first_name != null) ?? companiesWithGivenRole[0];
  return contact || {};
}

export function getNestedContactByRoleOrAll(
  companies: Array<{ role_desc: string; first_name: string; children: any[] }>,
  roleDescription: string
) {
  const companiesWithGivenRole = companies.filter(
    company => byLowerRoleDesc(roleDescription, company) && company.children.length > 0
  );
  const contactsWithGivenRole = companiesWithGivenRole.reduce(
    (acc, nextCompany) => [...acc, ...(nextCompany.children || [])],
    []
  );
  const isPrimary = c => c.primary_f;
  const isCbre = c => c.company_legal_name === 'CBRE';
  const sortPredicate = (a, b) => a.party_organization_display_name - b.party_organization_display_name;
  const anyPrimaryContacts = contactsWithGivenRole.filter(c => isPrimary(c));
  const cbreNonPrimaryContacts = contactsWithGivenRole.filter(c => isCbre(c) && !isPrimary(c));
  const nonCbreNonPrimaryContacts = contactsWithGivenRole.filter(c => !isCbre(c) && !isPrimary(c));
  const nonCbreNonPrimaryContactsSorted = nonCbreNonPrimaryContacts.sort(sortPredicate);
  if (anyPrimaryContacts.length > 0) {
    return anyPrimaryContacts;
  }
  return [...cbreNonPrimaryContacts, ...nonCbreNonPrimaryContactsSorted];
}

export interface WithCompanyContactsDef {
  companyContacts: () => Array<{ [key: string]: any }>;
  findContactInRoles: (roles: string[]) => Array<{ [key: string]: any }>;
  getUniqueCompaniesRoles: () => Array<{ [key: string]: any }>;
  getUniqueContactsRoles: () => Array<{ [key: string]: any }>;
  findContactByRole: (role: string) => Array<{ [key: string]: any }>;
  findOneContactByRole: (role: string) => { [key: string]: any };
  findByMatch: (match: { [key: string]: any }) => { [key: string]: any };
  addCompany: (company: { [key: string]: any }) => Array<{ [key: string]: any }>;
  addContact: (company: { [key: string]: any }) => Array<{ [key: string]: any }>;
}
/**
 * CompanyContacts helpers
 */
export const withCompanyContacts = (): WithCompanyContactsDef => {
  function companyContacts() {
    return safeIterableContacts(this);
  }
  function findContactInRoles(roles: string[]) {
    return safeIterableContacts(this).filter(contact => roles.some(role => byLowerRoleDesc(role, contact)));
  }
  function findContactByRole(role: string) {
    return safeIterableContacts(this).filter(byLowerRoleDesc.bind(null, role));
  }
  function findOneContactByRole(role: string): { [key: string]: any } {
    return safeIterableContacts(this).find(byLowerRoleDesc.bind(null, role));
  }
  function findByMatch(targetCompanyContact: { [key: string]: any }): { [key: string]: any } {
    return safeIterableContacts(this).find(companyContact => {
      return companyContactMatcher(targetCompanyContact, companyContact);
    });
  }

  function getUniqueCompaniesRoles() {
    return Object.values(
      Object.assign({}, ...safeIterableContacts(this).map(c => ({ [`${c.company_id},${c.role_desc}`]: c })))
    );
  }

  /**
   * Returns a list of unique companies using company_id and role_desc as the main grouping key, however
   * when a contact_id exists, it should override existing company without contact_id
   * @param usage
   */

  function getUniqueContactsRoles() {
    const uniqueRolesMap = new Map<string, { company_id: number; role_desc: string; contact_id: number }[]>();
    safeIterableContacts(this).forEach(contact => {
      const key = `${contact.company_id}.${contact.role_desc}`;
      if (uniqueRolesMap.has(key)) {
        // when more than one company/role exists, the one with contact_id should override the one without
        uniqueRolesMap.set(
          key,
          uniqueRolesMap
            .get(key)
            .concat(contact)
            .filter(c => c.contact_id != null)
        );
      } else {
        // add ass is even if no contact_id is present
        uniqueRolesMap.set(key, [contact]);
      }
    });
    return Array.from(uniqueRolesMap.values()).reduce((acc, val) => acc.concat(val), []);
  }

  function addCompany(company): Array<{ [key: string]: any }> {
    // skip add if no company id or role provided
    if (!company || !company.company_id || !company.role_desc) {
      return this.company_contact_role_addresses;
    }
    // eslint-disable-next-line no-unused-vars
    const { contact_id, ...onlyCompanyAttrs } = company;
    // to find we need to ignore contact_id
    const existing = findByMatch.call(this, onlyCompanyAttrs);
    if (!existing) {
      this.company_contact_role_addresses = [...safeIterableContacts(this), company];
    }
    return this.company_contact_role_addresses;
  }

  function addContact(contact): Array<{ [key: string]: any }> {
    const { contact_id, ...company } = contact;
    if (!contact_id) {
      return this.company_contact_role_addresses;
    }
    const existingContact = findByMatch.call(this, contact);
    const existingCompany = findByMatch.call(this, company);
    if (!existingContact && (!existingCompany || (existingCompany && existingCompany.contact_id))) {
      this.company_contact_role_addresses = [...safeIterableContacts(this), contact];
    } else if (existingCompany) {
      this.company_contact_role_addresses = safeIterableContacts(this).map(persistedCompany => {
        return companyContactMatcher(persistedCompany, company) ? contact : persistedCompany;
      });
    }
    return this.company_contact_role_addresses;
  }

  return {
    companyContacts,
    findContactInRoles,
    findContactByRole,
    findOneContactByRole,
    getUniqueCompaniesRoles,
    getUniqueContactsRoles,
    addCompany,
    addContact,
    findByMatch,
  };
};
